import { useEffect, useState } from "react";
import { http, money, mesi, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export const CompensiOperatori = ({ year, month }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});

  const load = async () => {
    if (!year || !month) return setData(null);
    const { data } = await http.get("/cleaner-payouts", { params: { year, month } });
    setData(data); setDraft({});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const save = async (o, patch) => {
    try {
      const { data } = await http.put("/cleaner-payouts", { year, month, cleaner_name: o.cleaner_name, ...patch });
      setData(data); setDraft({}); toast.success("Compenso operatore salvato (rendiconto non modificato)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!year || !month) return (
    <Card className="p-4 rounded-2xl" data-testid="compensi-operatori">
      <div className="font-display font-bold text-[var(--teal-deep)]">Compensi operatori</div>
      <div className="text-sm text-slate-500 mt-1">Seleziona mese e anno nei filtri per vedere i compensi.</div>
    </Card>
  );

  if (!data) return null;

  return (
    <Card className="p-4 rounded-2xl" data-testid="compensi-operatori">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="font-display text-lg font-bold text-[var(--teal-deep)]">Compensi operatori · {mesi[month]} {year}</div>
          <div className="text-xs text-slate-500">Dato operativo interno: modificare il compenso non cambia il rendiconto né le pulizie addebitate al proprietario.</div>
        </div>
        <div className="flex gap-4 text-right">
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Calcolato</div><div className="font-display text-lg font-extrabold tabular-nums">{money(data.totale_calcolato)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Effettivo</div><div className="font-display text-lg font-extrabold tabular-nums" data-testid="payout-tot-effettivo">{money(data.totale_effettivo)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Da pagare</div><div className="font-display text-lg font-extrabold tabular-nums text-amber-700">{money(data.totale_da_pagare)}</div></div>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-[11px] uppercase text-slate-500"><th className="py-2">Operatore</th><th>Interventi</th><th className="text-right">Calcolato</th><th className="text-right">Effettivo</th><th>Stato</th><th></th></tr></thead>
          <tbody>
            {data.operatori.length === 0 && <tr><td colSpan={6} className="py-4 text-slate-400">Nessun intervento registrato in questo mese.</td></tr>}
            {data.operatori.map((o) => {
              const val = draft[o.cleaner_name] !== undefined ? draft[o.cleaner_name] : o.effettivo;
              return (
                <tr key={o.cleaner_name} className="border-t border-slate-100" data-testid={`payout-${o.cleaner_name}`}>
                  <td className="py-2 font-medium">{o.cleaner_name}</td>
                  <td>{o.interventi}</td>
                  <td className="text-right tabular-nums text-slate-500">{money(o.calcolato)}</td>
                  <td className="text-right">
                    <Input type="number" step="0.01" value={val}
                      onChange={(e) => setDraft({ ...draft, [o.cleaner_name]: e.target.value })}
                      className="h-9 w-28 text-right tabular-nums inline-block" data-testid={`payout-input-${o.cleaner_name}`} />
                  </td>
                  <td>
                    <Badge variant="outline" className={o.stato === "pagato" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"} data-testid={`payout-stato-${o.cleaner_name}`}>
                      {o.stato === "pagato" ? "Pagato" : "Da pagare"}
                    </Badge>
                    {o.override && <span className="ml-2 text-[10px] text-slate-400">override admin</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="rounded-lg h-8 mr-1" data-testid={`payout-save-${o.cleaner_name}`}
                      onClick={() => save(o, { effettivo: Number(val) })}>Salva</Button>
                    <Button size="sm" variant="outline" className="rounded-lg h-8 mr-1" data-testid={`payout-toggle-${o.cleaner_name}`}
                      onClick={() => save(o, { stato: o.stato === "pagato" ? "da_pagare" : "pagato" })}>
                      <Check className="h-3.5 w-3.5 mr-1" />{o.stato === "pagato" ? "Da pagare" : "Segna pagato"}
                    </Button>
                    {o.override && <Button size="sm" variant="ghost" className="h-8" title="Torna al calcolato"
                      onClick={() => save(o, { effettivo: null })} data-testid={`payout-reset-${o.cleaner_name}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default CompensiOperatori;
